import { Pause, Play } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Calorimeter3D } from './Calorimeter3D';
import {
  CALORIMETRY_CHALLENGES,
  CALORIMETRY_FIELDS,
  CALORIMETRY_QUESTION,
  CALORIMETRY_PRESETS,
  DEFAULT_CALORIMETRY_SETUP,
  LIQUID_RANGE_C,
  calculateCalorimetryExplore,
  type CalorimetryPreset,
  type CalorimetrySetup,
} from './calorimetryExplore';
import { formatSig, formatSigned } from './energyCalorimetry';
import { EnergyLevelDiagram, ThermometerReading } from './EnergyCalorimetryVisuals';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';

/**
 * Explore view of coffee-cup calorimetry (CT-INTERACT): the learner types the volume, both
 * thermometer readings and the amount reacted. One setup drives the worked calculation, the
 * thermometers, the energy-level diagram and the 3D calorimeter.
 */
export function CalorimetryExplore() {
  const [setup, setSetup] = useState<CalorimetrySetup>(DEFAULT_CALORIMETRY_SETUP);
  const [preset, setPreset] = useState<CalorimetryPreset | null>(CALORIMETRY_PRESETS[0]);
  const [presetVersion, setPresetVersion] = useState(0);
  const [playing, setPlaying] = useState(true);
  const result = useMemo(() => calculateCalorimetryExplore(setup), [setup]);

  const choosePreset = (entry: CalorimetryPreset) => {
    setPreset(entry);
    setSetup(entry.setup);
    setPresetVersion((version) => version + 1);
  };

  const change = setup.finalC - setup.initialC;
  const afterTone = change > 0 ? 'warmer' : change < 0 ? 'cooler' : 'start';
  const sceneState = useMemo(
    () => ({ volumeMl: setup.volumeMl, startTemperatureC: setup.initialC, temperatureC: setup.finalC }),
    [setup],
  );
  const deltaHText =
    result.deltaHkJPerMol === null ? undefined : `ΔH = ${formatSigned(result.deltaHkJPerMol, 3)} kJ/mol`;

  return (
    <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
      <aside className="grid content-start gap-4">
        <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Presets</p>
          <p className="mt-1 text-xs font-medium leading-5 text-slate2">
            Shortcuts from the reviewed coffee-cup cases. They fill in the readings; you can change any of them.
          </p>
          <div className="mt-3 grid gap-2" aria-label="Calorimetry presets">
            {CALORIMETRY_PRESETS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                aria-pressed={preset?.id === entry.id}
                onClick={() => choosePreset(entry)}
                className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold ${preset?.id === entry.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
              >
                {entry.name}
              </button>
            ))}
          </div>
        </section>
        <ModelInputPanel
          key={presetVersion}
          legend="Your calorimetry readings"
          fields={CALORIMETRY_FIELDS}
          values={setup}
          onChange={(next) => {
            setSetup(next as CalorimetrySetup);
            setPreset(null);
          }}
          sliderKey="finalC"
        />
        <ExploreChallenges question={CALORIMETRY_QUESTION} challenges={CALORIMETRY_CHALLENGES} result={result} />
      </aside>

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xl font-extrabold text-ink">Coffee-cup calorimeter</h3>
          <button
            type="button"
            aria-pressed={!playing}
            onClick={() => setPlaying((value) => !value)}
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink"
          >
            {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {playing ? 'Pause animation' : 'Play animation'}
          </button>
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_200px]">
          <Calorimeter3D
            state={sceneState}
            playing={playing}
            label={`Coffee-cup calorimeter with ${formatSig(setup.volumeMl, 3)} mL of solution; the thermometer reads ${setup.finalC.toFixed(1)} °C`}
          />
          <div className="flex items-end justify-center gap-6 rounded-2xl bg-surface p-4">
            <ThermometerReading label="Initial" temperatureC={setup.initialC} tone="start" maxC={LIQUID_RANGE_C.max} />
            <ThermometerReading label="Final" temperatureC={setup.finalC} tone={afterTone} maxC={LIQUID_RANGE_C.max} />
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)]">
          <WorkedStepsPanel
            title="How the model works it out"
            steps={result.steps}
            stepTestIdPrefix="calorimetry-step"
            result={`Result: ${result.result}`}
            resultTestId="calorimetry-result"
            limitNote={result.limitNote}
          />
          <div className="grid content-start gap-3">
            {result.direction ? (
              <EnergyLevelDiagram direction={result.direction} deltaHText={deltaHText} />
            ) : (
              <p className="rounded-2xl border border-dashed border-hairline bg-surface p-4 text-sm font-bold text-slate2">
                The energy-level diagram needs a ΔH. Change a reading so the model can work one out.
              </p>
            )}
            {preset && result.deltaHkJPerMol !== null ? (
              <p className="rounded-2xl bg-surface p-3 text-xs font-bold leading-5 text-slate2" data-testid="calorimetry-reference">
                Reference: {formatSigned(preset.referenceDeltaHkJ, 3)} kJ/mol per mol of {preset.perMolOf} · your
                result is {formatSig((Math.abs(result.deltaHkJPerMol) / Math.abs(preset.referenceDeltaHkJ)) * 100, 3)}% of
                its size. Real cups lose some energy, so measured |ΔH| is usually smaller.
              </p>
            ) : null}
          </div>
        </div>
        <p className="mt-4 text-xs font-medium leading-5 text-slate2">
          Sign convention: q is the energy gained by the solution. The reacting system loses that energy, so
          ΔH = −q ÷ n. This Explore view does not record progress.
        </p>
      </section>
    </div>
  );
}
